import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { airtableService } from '../services/airtable';
import DiagnosticPanel from './DiagnosticPanel';
import TableStatusPanel from './TableStatusPanel';
import NotificationSettings from './NotificationSettings';
import { formatToLocalTime, formatToLocalDateTime, getMinutesFromNow, formatWaitTime, getCurrentUTCTime } from '../utils/timeUtils';
import { notificationService } from '../services/notificationService';
import { pushService } from '../services/pushService';

const DrGrantDashboard = ({ user, currentSession, onSessionUpdate, onLogout }) => {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const previousQueueRef = useRef([]);
  const [error, setError] = useState('');
  const [sessionForm, setSessionForm] = useState({
    accessCode: '',
    clinicCode: '',
    durationMinutes: 180 // default 3 hours
  });
  const [showSessionForm, setShowSessionForm] = useState(!currentSession);
  const [existingSessions, setExistingSessions] = useState([]);
  const [showExistingSessions, setShowExistingSessions] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationSettings, setNotificationSettings] = useState(null);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Initialize notifications
    initializeNotifications();

    if (currentSession) {
      loadQueue();
      const interval = setInterval(loadQueue, 3000); // Refresh every 3 seconds
      return () => clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession]);

  // Auto-close session timer
  useEffect(() => {
    if (!currentSession?.scheduledEndTime) {
      setTimeRemaining(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const endTime = new Date(currentSession.scheduledEndTime);
      const diffMs = endTime - now;

      if (diffMs <= 0) {
        // Time's up - auto-close the session
        setTimeRemaining(null);
        autoEndSession();
        return;
      }

      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m remaining`);
      } else if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s remaining`);
      } else {
        setTimeRemaining(`${seconds}s remaining`);
      }
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession?.scheduledEndTime]);

  const autoEndSession = async () => {
    if (!currentSession) return;
    try {
      await airtableService.endSession(currentSession.sessionId, getCurrentUTCTime());
      onSessionUpdate(null);
      navigate('/dr-grant/summary');
    } catch (error) {
      console.error('Error auto-ending session:', error);
      setError('Session auto-close failed. Please end the session manually.');
    }
  };

  const initializeNotifications = async () => {
    try {
      await notificationService.initialize();
      const settings = notificationService.getSettings();
      setNotificationSettings(settings);

      // Auto-request permission if notifications are enabled but permission not granted
      if (settings.enabled && !notificationService.hasPermission()) {
        await requestNotificationPermission();
      }

      // Keep the push hub's copy of this device's subscription fresh, and
      // clear the icon badge now that the queue is on screen.
      await pushService.syncRegistration();
      if ('clearAppBadge' in navigator) {
        navigator.clearAppBadge().catch(() => {});
      }
    } catch (error) {
      console.error('Failed to initialize notifications:', error);
    }
  };

  const requestNotificationPermission = async () => {
    try {
      const permission = await notificationService.requestPermission();
      if (permission === 'granted') {
        console.log('Notification permission granted');
        return true;
      } else {
        console.log('Notification permission denied');
        // Update settings to reflect that notifications are disabled
        const settings = notificationService.getSettings();
        settings.enabled = false;
        notificationService.saveSettings(settings);
        setNotificationSettings(settings);
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  };

  const loadQueue = async () => {
    if (!currentSession?.sessionId) return;

    try {
      const queueRecords = await airtableService.getQueueForSession(currentSession.sessionId);
      
      // Check for new students (only if we had a previous queue to compare)
      if (previousQueueRef.current.length > 0) {
        const previousIds = new Set(previousQueueRef.current.map(student => student.id));
        const newStudents = queueRecords.filter(student => !previousIds.has(student.id));
        
        // Create notifications for new students
        newStudents.forEach(async (student) => {
          const studentName = student.fields['Student Name'];
          const isEmergency = student.fields['Is Emergency'] || false;
          const reason = student.fields['Queue Reason'];
          const operatory = student.fields['Operatory Number'];

          // In-app notification
          let message = `${studentName} joined the queue`;
          if (operatory) message += ` (Op #${operatory})`;
          if (reason) message += ` - ${reason}`;

          const notification = {
            id: Date.now() + Math.random(),
            message,
            isEmergency,
            timestamp: Date.now()
          };
          
          setNotifications(prev => [...prev, notification]);
          
          // Always play sound when a student joins
          await notificationService.playNotificationSound(isEmergency);

          // Web push notification (with buzz) - read settings fresh to avoid stale state
          const currentSettings = notificationService.getSettings();
          if (currentSettings?.enabled && notificationService.hasPermission()) {
            if ((isEmergency && currentSettings.emergencies) ||
                (!isEmergency && currentSettings.newStudents)) {
              await notificationService.notifyNewStudent(studentName, isEmergency);
            }
          }
          
          // Auto-remove in-app notification after 10 seconds
          setTimeout(() => {
            setNotifications(prev => prev.filter(n => n.id !== notification.id));
          }, 10000);
        });
      }
      
      // Store current queue for next comparison using ref (synchronous)
      previousQueueRef.current = queueRecords;
      setQueue(queueRecords);
    } catch (error) {
      console.error('Error loading queue:', error);
    }
  };

  const dismissNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const loadExistingSessions = async () => {
    try {
      setLoading(true);
      const activeSessions = await airtableService.getActiveSessions();
      setExistingSessions(activeSessions);
      setShowExistingSessions(true);
    } catch (error) {
      console.error('Error loading existing sessions:', error);
      setError('Failed to load existing sessions.');
    } finally {
      setLoading(false);
    }
  };

  const joinExistingSession = async (session) => {
    try {
      setLoading(true);
      const sessionData = {
        sessionId: session.fields['Session ID'],
        doctorName: user.name,
        accessCode: session.fields['Access Code'],
        clinicCode: session.fields['Clinic Code'],
        startTime: session.fields['Start Time']
      };
      
      console.log('🔗 Joining existing session:', sessionData);
      onSessionUpdate(sessionData);
      setShowSessionForm(false);
      setShowExistingSessions(false);

      // Auto-enable notifications when joining a session
      const settings = notificationService.getSettings();
      if (!settings.enabled) {
        const enabled = await notificationService.enable();
        if (enabled) {
          setNotificationSettings({ ...settings, enabled: true });
        }
      }
    } catch (error) {
      console.error('Error joining session:', error);
      setError('Failed to join session.');
    } finally {
      setLoading(false);
    }
  };

  const startSession = async (e) => {
    e.preventDefault();
    
    console.log('🚀 START SESSION CLICKED');
    console.log('Form data:', sessionForm);
    console.log('Airtable service initialized:', airtableService.isInitialized);
    console.log('User:', user);
    
    if (!sessionForm.accessCode.trim() || !sessionForm.clinicCode.trim()) {
      console.log('❌ Validation failed: missing fields');
      setError('Please fill in all fields');
      return;
    }

    if (sessionForm.accessCode.length !== 4 || !/^\d+$/.test(sessionForm.accessCode)) {
      console.log('❌ Validation failed: invalid access code');
      setError('Access code must be exactly 4 digits');
      return;
    }

    console.log('✅ Validation passed, starting session creation...');
    setLoading(true);
    setError('');

    try {
      // First verify Airtable connection
      if (!airtableService.isInitialized) {
        console.error('❌ Airtable service not initialized');
        setError('Airtable not connected. Please check your setup.');
        setLoading(false);
        return;
      }

      const sessionId = `session_${Date.now()}`;
      const startTime = getCurrentUTCTime();
      let scheduledEndTime = null;
      if (sessionForm.durationMinutes > 0) {
        const endDate = new Date(new Date(startTime).getTime() + sessionForm.durationMinutes * 60 * 1000);
        scheduledEndTime = endDate.toISOString();
      }

      const sessionData = {
        sessionId,
        doctorName: user.name,
        accessCode: sessionForm.accessCode,
        clinicCode: sessionForm.clinicCode.toUpperCase(),
        startTime,
        scheduledEndTime
      };

      console.log('📝 Attempting to create session with data:', sessionData);
      
      const sessionRecord = await airtableService.createSession(sessionData);
      console.log('📊 Session creation result:', sessionRecord);
      
      if (sessionRecord) {
        console.log('✅ Session created successfully! Updating app state...');
        console.log('📞 Calling onSessionUpdate with:', sessionData);
        onSessionUpdate(sessionData);
        console.log('🔄 Setting showSessionForm to false');
        setShowSessionForm(false);
        console.log('🎉 Session start complete!');

        // Auto-enable notifications for the doctor on session start
        const settings = notificationService.getSettings();
        if (!settings.enabled) {
          const enabled = await notificationService.enable();
          if (enabled) {
            setNotificationSettings({ ...settings, enabled: true });
          }
        }
      } else {
        console.error('❌ Session creation returned null/false');
        setError('Failed to start session. Check console for details.');
      }
    } catch (error) {
      console.error('💥 Error starting session:', error);
      console.error('Error stack:', error.stack);
      setError(`Unable to start session: ${error.message}`);
    } finally {
      console.log('🏁 Setting loading to false');
      setLoading(false);
    }
  };

  const endSession = async () => {
    if (!currentSession) return;

    const confirmEnd = window.confirm(
      'Are you sure you want to end this session? This will close the queue for new students.'
    );
    
    if (!confirmEnd) return;

    setLoading(true);

    try {
      await airtableService.endSession(currentSession.sessionId, getCurrentUTCTime());
      onSessionUpdate(null);
      navigate('/dr-grant/summary');
    } catch (error) {
      console.error('Error ending session:', error);
      setError('Failed to end session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStudentClick = (student) => {
    navigate(`/dr-grant/evaluate/${student.id}`);
  };

  const removeFromQueue = async (studentId) => {
    const confirmRemove = window.confirm('Remove this student from the queue?');
    if (!confirmRemove) return;

    try {
      await airtableService.removeFromQueue(studentId);
      loadQueue();
    } catch (error) {
      console.error('Error removing student:', error);
      setError('Failed to remove student from queue.');
    }
  };

  const skipEvaluation = async (student) => {
    const confirmSkip = window.confirm(
      `Mark ${student.fields['Student Name']} as "no review needed"? A record will be saved without a rubric score.`
    );
    if (!confirmSkip) return;

    const reason = window.prompt('Optional note (e.g., "quick check-in", "consult only"):', '') || '';

    try {
      const joinTime = student.fields['Join Time'];
      const responseTimeMinutes = joinTime ? getMinutesFromNow(joinTime) : 0;

      const evaluationData = {
        sessionId: currentSession.sessionId,
        studentName: student.fields['Student Name'],
        queueRecordId: student.id,
        yearLevel: student.fields['Year Level'] || '',
        requeueStudent: false,
        evaluationSkipped: true,
        notes: reason.trim() ? `No review needed — ${reason.trim()}` : 'No review needed',
        evaluationTime: getCurrentUTCTime(),
        responseTimeMinutes
      };

      const evalRecord = await airtableService.createEvaluation(evaluationData);
      if (!evalRecord) {
        setError('Failed to log the no-review entry. Please try again.');
        return;
      }

      await airtableService.updateQueueStatus(student.id, 'completed');
      loadQueue();
    } catch (error) {
      console.error('Error skipping evaluation:', error);
      setError('Failed to mark student as no review needed.');
    }
  };

  const addStudentToQueue = async () => {
    const studentName = prompt('Enter student initials or nickname to add to queue:');
    if (!studentName?.trim()) return;

    const yearLevel = prompt('Enter year level (D3 or D4):', 'D3');
    const validLevel = (yearLevel === 'D3' || yearLevel === 'D4') ? yearLevel : 'D3';

    try {
      const currentQueue = await airtableService.getQueueForSession(currentSession.sessionId);
      const nextPosition = currentQueue.length + 1;

      const queueData = {
        sessionId: currentSession.sessionId,
        studentName: studentName.trim(),
        clinicCode: currentSession.clinicCode,
        joinTime: getCurrentUTCTime(),
        position: nextPosition,
        yearLevel: validLevel
      };

      await airtableService.addToQueue(queueData);
      loadQueue();
    } catch (error) {
      console.error('Error adding student:', error);
      setError('Failed to add student to queue.');
    }
  };

  const moveStudent = async (studentId, direction) => {
    const currentIndex = queue.findIndex(q => q.id === studentId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= queue.length) return;

    const newOrder = [...queue];
    [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];

    try {
      await airtableService.reorderQueue(currentSession.sessionId, newOrder);
      setQueue(newOrder);
    } catch (error) {
      console.error('Error reordering queue:', error);
      setError('Failed to reorder queue.');
    }
  };

  const formatTime = (timeString) => {
    return formatToLocalTime(timeString);
  };

  const calculateWaitTime = (joinTime) => {
    const minutes = getMinutesFromNow(joinTime);
    return formatWaitTime(minutes);
  };

  const getQueueStats = () => {
    const waiting = queue.filter(q => q.fields['Status'] === 'waiting').length;
    const inProgress = queue.filter(q => q.fields['Status'] === 'in_progress').length;
    const avgWaitTime = queue.length > 0 ? 
      queue.reduce((acc, q) => {
        const wait = getMinutesFromNow(q.fields['Join Time']);
        return acc + wait;
      }, 0) / queue.length : 0;

    return { waiting, inProgress, avgWaitTime: Math.round(avgWaitTime) };
  };

  if (showSessionForm) {
    return (
      <div className="container">
        <DiagnosticPanel />
        <div className="header">
          <h1>Start New Session</h1>
          <p>Welcome, {user.name}</p>
        </div>
        
        <TableStatusPanel />
        
        
        {/* Existing Sessions Panel */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <h3 style={{ margin: 0, color: 'var(--text-dark)' }}>
              🔗 Join Existing Session
            </h3>
            <button 
              onClick={loadExistingSessions}
              disabled={loading}
              className="btn btn-success"
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              {loading ? 'Loading...' : 'Show Active Sessions'}
            </button>
          </div>
          
          {showExistingSessions && (
            <div>
              {existingSessions.length === 0 ? (
                <p style={{ color: 'var(--text-light)', margin: 0 }}>
                  No active sessions found. You can start a new session below.
                </p>
              ) : (
                <div>
                  <p style={{ color: 'var(--text-dark)', marginBottom: '12px' }}>
                    Click on a session to join it:
                  </p>
                  {existingSessions.map((session) => (
                    <div
                      key={session.id}
                      style={{
                        padding: '12px',
                        marginBottom: '8px',
                        backgroundColor: 'var(--warm-cream)',
                        borderRadius: '6px',
                        border: '1px solid var(--light-blue)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => joinExistingSession(session)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 'bold', color: 'var(--text-dark)' }}>
                            Access Code: {session.fields['Access Code']} | Clinic: {session.fields['Clinic Code']}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                            Started: {formatToLocalDateTime(session.fields['Start Time'])}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                            Doctor: {session.fields['Doctor Name']}
                          </div>
                        </div>
                        <div style={{
                          padding: '4px 8px',
                          backgroundColor: 'var(--muted-sage)',
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          Click to Join
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => setShowExistingSessions(false)}
                    style={{
                      marginTop: '8px',
                      padding: '6px 12px',
                      border: 'none',
                      backgroundColor: 'var(--text-light)',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Hide Sessions
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="card">
          <h3 style={{ marginBottom: '16px', color: 'var(--text-dark)' }}>
            🆕 Or Start New Session
          </h3>
          <form onSubmit={startSession}>
            <div className="input-group">
              <label htmlFor="accessCode">4-Digit Access Code for Students</label>
              <input
                type="text"
                id="accessCode"
                name="accessCode"
                value={sessionForm.accessCode}
                onChange={(e) => setSessionForm({...sessionForm, accessCode: e.target.value})}
                placeholder="Enter 4-digit code (e.g., 1234)"
                maxLength="4"
                disabled={loading}
              />
            </div>
            
            <div className="input-group">
              <label htmlFor="clinicCode">Clinic Code</label>
              <input
                type="text"
                id="clinicCode"
                name="clinicCode"
                value={sessionForm.clinicCode}
                onChange={(e) => setSessionForm({...sessionForm, clinicCode: e.target.value})}
                placeholder="Enter clinic code (e.g., CLINIC-A)"
                disabled={loading}
              />
            </div>

            <div className="input-group">
              <label htmlFor="durationMinutes">Session Duration (auto-close)</label>
              <select
                id="durationMinutes"
                value={sessionForm.durationMinutes}
                onChange={(e) => setSessionForm({...sessionForm, durationMinutes: Number(e.target.value)})}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e1e8ed',
                  borderRadius: '8px',
                  fontSize: '16px',
                  backgroundColor: 'white',
                  color: 'var(--text-dark)'
                }}
              >
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
                <option value={120}>2 hours</option>
                <option value={150}>2.5 hours</option>
                <option value={180}>3 hours</option>
                <option value={240}>4 hours</option>
                <option value={300}>5 hours</option>
                <option value={0}>No auto-close</option>
              </select>
              <small style={{ color: 'var(--text-light)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                The session will automatically close after this time to prevent students from logging into old sessions.
              </small>
            </div>

            {error && (
              <div style={{ 
                color: 'var(--coral)', 
                marginBottom: '16px', 
                padding: '12px', 
                backgroundColor: '#ffe6e6', 
                borderRadius: '6px' 
              }}>
                {error}
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading}
                style={{ flex: 1 }}
                onClick={() => console.log('🖱️ Start Session button clicked!')}
              >
                {loading ? 'Starting...' : 'Start Session'}
              </button>
              
              
              <button
                type="button"
                onClick={() => navigate('/dr-grant/history')}
                className="btn btn-primary"
              >
                Past Sessions
              </button>

              <button
                type="button"
                onClick={onLogout}
                className="btn btn-secondary"
              >
                Logout
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  const stats = getQueueStats();

  return (
    <div className="container">
      <div className="header">
        <h1>Queue Dashboard</h1>
        <p>Session: {currentSession?.clinicCode} | Code: {currentSession?.accessCode}</p>
        {timeRemaining && (
          <p style={{
            color: timeRemaining.startsWith('0') || timeRemaining.includes('s remaining') ? 'var(--coral)' : 'var(--muted-sage)',
            fontWeight: 'bold',
            fontSize: '14px',
            margin: '4px 0 0 0'
          }}>
            {timeRemaining}
          </p>
        )}
        {currentSession?.scheduledEndTime === null && (
          <p style={{ color: 'var(--text-light)', fontSize: '12px', margin: '4px 0 0 0' }}>
            No auto-close set
          </p>
        )}
      </div>

      {/* Notifications */}
      {notifications.length > 0 && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 1000, maxWidth: '400px' }}>
          {notifications.map(notification => (
            <div
              key={notification.id}
              style={{
                backgroundColor: notification.isEmergency ? '#ff6b6b' : '#4ecdc4',
                color: 'white',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                animation: 'slideIn 0.3s ease-out'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: '8px', fontSize: '18px' }}>
                  {notification.isEmergency ? '🚨' : '👋'}
                </span>
                <span>{notification.message}</span>
              </div>
              <button
                onClick={() => dismissNotification(notification.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '0 4px',
                  opacity: 0.8
                }}
                onMouseOver={(e) => e.target.style.opacity = 1}
                onMouseOut={(e) => e.target.style.opacity = 0.8}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ 
          color: 'var(--coral)', 
          marginBottom: '16px', 
          padding: '12px', 
          backgroundColor: '#ffe6e6', 
          borderRadius: '6px' 
        }}>
          {error}
        </div>
      )}

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <h3 style={{ color: 'var(--primary-blue)', fontSize: '2em', margin: '0 0 8px 0' }}>
            {stats.waiting}
          </h3>
          <p style={{ color: 'var(--text-dark)', margin: 0 }}>Waiting</p>
        </div>
        
        <div className="card" style={{ textAlign: 'center' }}>
          <h3 style={{ color: 'var(--coral)', fontSize: '2em', margin: '0 0 8px 0' }}>
            {stats.inProgress}
          </h3>
          <p style={{ color: 'var(--text-dark)', margin: 0 }}>In Progress</p>
        </div>
        
        <div className="card" style={{ textAlign: 'center' }}>
          <h3 style={{ color: 'var(--muted-sage)', fontSize: '2em', margin: '0 0 8px 0' }}>
            {stats.avgWaitTime}m
          </h3>
          <p style={{ color: 'var(--text-dark)', margin: 0 }}>Avg Wait</p>
        </div>
      </div>

      {/* QR Code Panel */}
      {showQRCode && currentSession && (
        <div className="card" style={{ textAlign: 'center' }}>
          <h3 style={{ color: 'var(--text-dark)', marginBottom: '12px' }}>
            Student Login QR Code
          </h3>
          <p style={{ color: 'var(--text-light)', marginBottom: '16px', fontSize: '14px' }}>
            Students can scan this code to go directly to the login page with session codes pre-filled.
          </p>
          <div style={{ display: 'inline-block', padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '2px solid var(--light-blue)' }}>
            <QRCodeSVG
              value={`${window.location.origin}/student/login?access=${currentSession.accessCode}&clinic=${currentSession.clinicCode}`}
              size={200}
              level="M"
            />
          </div>
          <p style={{ color: 'var(--text-light)', marginTop: '12px', fontSize: '12px' }}>
            Access Code: <strong>{currentSession.accessCode}</strong> | Clinic: <strong>{currentSession.clinicCode}</strong>
          </p>
          <button
            onClick={() => setShowQRCode(false)}
            style={{
              marginTop: '8px',
              padding: '6px 12px',
              border: 'none',
              backgroundColor: 'var(--text-light)',
              color: 'white',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Hide QR Code
          </button>
        </div>
      )}

      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <h2 style={{ color: 'var(--text-dark)', margin: 0 }}>
            Student Queue ({queue.length})
          </h2>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={addStudentToQueue}
              className="btn btn-success"
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              Add Student
            </button>

            <button
              onClick={() => setShowNotificationSettings(true)}
              className="btn btn-primary"
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              {notificationSettings?.enabled ? 'Notifications On' : 'Notifications Off'}
            </button>

            <button
              onClick={() => setShowQRCode(!showQRCode)}
              className="btn btn-primary"
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              {showQRCode ? 'Hide QR' : 'QR Code'}
            </button>

            <button
              onClick={() => navigate('/dr-grant/summary')}
              className="btn btn-success"
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              View Summary
            </button>

            <button
              onClick={() => navigate('/dr-grant/history')}
              className="btn btn-primary"
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              Past Sessions
            </button>

            <button
              onClick={endSession}
              className="btn btn-secondary"
              disabled={loading}
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              End Session
            </button>

            <button
              onClick={onLogout}
              className="btn btn-secondary"
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              Logout
            </button>
          </div>
        </div>

        {queue.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            color: 'var(--text-light)' 
          }}>
            <p>No students in queue yet.</p>
            <p>Share access code <strong>{currentSession?.accessCode}</strong> and clinic code <strong>{currentSession?.clinicCode}</strong> with students.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {queue.map((student, index) => (
              <div 
                key={student.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px',
                  backgroundColor: student.fields['Is Emergency'] ? '#ffe6e6' : 
                                 student.fields['Status'] === 'in_progress' ? 'var(--warm-cream)' : 'white',
                  border: `2px solid ${
                    student.fields['Is Emergency'] ? '#ff4444' :
                    student.fields['Status'] === 'in_progress' ? 'var(--coral)' : 
                    student.fields['Status'] === 'waiting' ? 'var(--light-blue)' : 'var(--muted-sage)'
                  }`,
                  borderRadius: '8px',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}
              >
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: 'bold', 
                  color: 'var(--primary-blue)',
                  minWidth: '40px'
                }}>
                  #{index + 1}
                </div>
                
                <div style={{ flex: 1, minWidth: '150px' }}>
                  <h4 style={{ margin: '0 0 4px 0', color: 'var(--text-dark)' }}>
                    {student.fields['Is Emergency'] && '🚨 '}{student.fields['Student Name']}
                    {student.fields['Year Level'] && (
                      <span style={{
                        marginLeft: '8px',
                        padding: '2px 8px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        borderRadius: '10px',
                        backgroundColor: student.fields['Year Level'] === 'D4' ? '#1976D2' : '#7B1FA2',
                        color: 'white'
                      }}>
                        {student.fields['Year Level']}
                      </span>
                    )}
                    {student.fields['Operatory Number'] && (
                      <span style={{ color: 'var(--primary-blue)', fontWeight: 'normal', marginLeft: '8px', fontSize: '14px' }}>
                        Op #{student.fields['Operatory Number']}
                      </span>
                    )}
                    {student.fields['Is Emergency'] && <span style={{ color: '#ff4444', fontWeight: 'bold', marginLeft: '8px' }}>EMERGENCY</span>}
                  </h4>
                  {student.fields['Queue Reason'] && (
                    <div style={{ color: 'var(--text-dark)', fontSize: '14px', marginBottom: '4px', fontStyle: 'italic' }}>
                      {student.fields['Queue Reason']}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <span style={{ color: 'var(--text-light)', fontSize: '14px' }}>
                      Joined: {formatTime(student.fields['Join Time'])}
                    </span>
                    <span style={{ color: 'var(--text-light)', fontSize: '14px' }}>
                      Wait: {calculateWaitTime(student.fields['Join Time'])}
                    </span>
                  </div>
                </div>
                
                <div style={{ 
                  padding: '4px 12px', 
                  borderRadius: '16px', 
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: 'white',
                  backgroundColor: 
                    student.fields['Status'] === 'waiting' ? 'var(--primary-blue)' :
                    student.fields['Status'] === 'in_progress' ? 'var(--coral)' : 'var(--muted-sage)'
                }}>
                  {student.fields['Status'] === 'waiting' ? 'Waiting' :
                   student.fields['Status'] === 'in_progress' ? 'In Progress' : 'Completed'}
                </div>
                
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {index > 0 && (
                    <button
                      onClick={() => moveStudent(student.id, 'up')}
                      style={{ 
                        padding: '4px 8px', 
                        border: 'none', 
                        borderRadius: '4px',
                        backgroundColor: 'var(--light-blue)', 
                        cursor: 'pointer' 
                      }}
                    >
                      ↑
                    </button>
                  )}
                  
                  {index < queue.length - 1 && (
                    <button
                      onClick={() => moveStudent(student.id, 'down')}
                      style={{ 
                        padding: '4px 8px', 
                        border: 'none', 
                        borderRadius: '4px',
                        backgroundColor: 'var(--light-blue)', 
                        cursor: 'pointer' 
                      }}
                    >
                      ↓
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleStudentClick(student)}
                    className="btn btn-primary"
                    style={{ fontSize: '12px', padding: '6px 12px' }}
                  >
                    {student.fields['Status'] === 'waiting' ? 'See Student' : 'View'}
                  </button>

                  <button
                    onClick={() => skipEvaluation(student)}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid var(--muted-sage)',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      color: 'var(--muted-sage)',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                    title="Mark as completed without a rubric evaluation"
                  >
                    No Review
                  </button>

                  <button
                    onClick={() => removeFromQueue(student.id)}
                    style={{
                      padding: '6px 12px',
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: 'var(--coral)',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notification Settings Modal */}
      <NotificationSettings 
        isOpen={showNotificationSettings}
        onClose={() => {
          setShowNotificationSettings(false);
          // Refresh settings after closing
          const settings = notificationService.getSettings();
          setNotificationSettings(settings);
        }}
      />
    </div>
  );
};

export default DrGrantDashboard;